import type { JSX } from "react";

import { Button, Card, Divider, Flex, Image, Text } from "@mantine/core";

import type { ChallengeInfo } from "@/sales-tools/challenges/api/getChallengesForEmployee";

import { CloseX } from "@/common-ui";
import { Calendar } from "@/sales-tools/challenges/icons/Calendar";
import { formatAnnouncementDateRange } from "@/sales-tools/challenges/utils/formatAnnouncementDateRange";
import { getBadgeIcon } from "@/sales-tools/challenges/utils/getBadgeIcon";
import { getChallengeDescription } from "@/sales-tools/challenges/utils/getChallengeDescription";
import { useLogger } from "@/sdk";

export function AnnouncementStartDetails({
    challenge,
    onDismiss,
}: {
    challenge: ChallengeInfo;
    onDismiss: () => void;
}): JSX.Element {
    const logger = useLogger({ module: "AnnouncementStartDetails", supportTeam: "sales" });
    const { challengeType, startDate, deadline, details, badgeType } = challenge;
    const isTeamChallenge = challengeType === "team";
    const { height, width } = getBadgeSize(badgeType);

    return (
        <Card w="450px" shadow="sm" padding="lg" radius="md" withBorder>
            <Flex justify="space-between">
                <Text
                    data-testid="announcement-challenge-details"
                    size="sm"
                    fw="var(--mantine-font-weight-heavy)"
                    c="dark.0"
                >
                    Challenge details
                </Text>
                <CloseX
                    data-testid="announcement-closex"
                    onClick={onDismiss}
                    style={{ cursor: "pointer" }}
                    fill="var(--mantine-color-gray-6)"
                />
            </Flex>
            <Text data-testid="announcement-details-challenge-name" mt="6px" mb="md" size="sm" c="dark.1">
                {getChallengeDescription(logger, challenge)}
            </Text>
            <Flex>
                <Calendar />
                <Text data-testid="announcement-challenge-date-range" ml="xxs" size="xs" c="dark.1">
                    {formatAnnouncementDateRange(new Date(startDate), new Date(deadline))}
                </Text>
            </Flex>
            {isTeamChallenge && (
                <>
                    <Divider style={{ alignSelf: "center" }} w="120%" color="#333F48" my="md" />
                    <Flex justify="space-around">
                        <Flex style={{ alignItems: "center" }} direction="column">
                            <Text
                                data-testid="announcement-team-1-name"
                                c="dark.0"
                                fw="var(--mantine-font-weight-heavy)"
                            >
                                {details.at(0)?.teamName}
                            </Text>
                        </Flex>
                        <Text style={{ alignSelf: "center" }} c="dark.2" fw="var(--mantine-font-weight-heavy)">
                            vs
                        </Text>

                        <Flex style={{ alignItems: "center" }} direction="column">
                            <Text
                                data-testid="announcement-team-2-name"
                                c="dark.0"
                                fw="var(--mantine-font-weight-heavy)"
                            >
                                {details.at(1)?.teamName}
                            </Text>
                        </Flex>
                    </Flex>
                    <Divider style={{ alignSelf: "center" }} w="120%" color="#333F48" my="md" />
                </>
            )}
            <Image
                mt="xxl"
                mb="md"
                style={{ alignSelf: "center" }}
                w={width}
                h={height}
                fit="scale-down"
                src={getBadgeIcon(badgeType)}
                data-testid="announcement-badge"
            />
            <Text
                data-testid="announcement-description"
                style={{ alignSelf: "center" }}
                size="sm"
                fw="var(--mantine-font-weight-heavy)"
                c="dark.1"
            >
                {isTeamChallenge ? "Winner earns a new badge" : "Complete the challenge to earn a new badge"}
            </Text>
            <Button
                data-testid="announcement-lets-go"
                onClick={onDismiss}
                style={{ alignSelf: "end" }}
                mt="4rem"
                maw="155px"
                radius="xl"
            >
                <Text size="sm" fw="var(--mantine-font-weight-heavy)">
                    Let’s go
                </Text>
            </Button>
        </Card>
    );
}

function getBadgeSize(badgeType: string): { height: number; width: number } {
    switch (badgeType) {
        case "SOLO_ANIMATED_BADGE":
        case "TEAM_ANIMATED_BADGE":
            return {
                height: 150,
                width: 150,
            };
        default:
            return {
                height: 80,
                width: 80,
            };
    }
}
